import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { useMemo, type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import {
  READING_MEASURE,
  AnswersMarkdown,
  Line,
  LinkClassContext,
  LinkPressContext,
} from './AnswersMarkdown';
import { SpecialistAvatar } from './SpecialistAvatar';
import { referenceLinkType, useAnswersPostId } from './answersAnalytics';
import { useAnswersFolds } from './answersFolds';
import {
  answerAnchor,
  answerBodyId,
  answerHeadingId,
  answerKey,
  followUpGroupName,
  replyDate,
  specialistName,
} from './answersLayout';
import {
  REFERENCE_KIND_BADGE,
  REFERENCE_KIND_LABELS,
  answerReferenceSection,
  referenceItemNodes,
  referenceKind,
} from './answersReferences';
import type { LibraryPostAnswer, LibraryPostReply } from '../../model/types';

import { CONVERSATION_BODY_TEXT_CLASS, ConversationEntry } from '@/components/ConversationEntry';
import { Heading } from '@/components/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';
import {
  trackInquiryLibraryAnswerCollapsed,
  trackInquiryLibraryAnswersToggled,
  trackInquiryLibraryReferenceClicked,
  trackInquiryLibraryReferencesExpanded,
} from '@/util/analyticsEvents';

/**
 * The fold control, a real button whose state a screen reader hears as expanded or collapsed. The
 * box is 48px tall, above the 44px minimum, because react-native-web ignores `hitSlop`. The
 * spoken label starts with the visible words.
 */
export function FoldButton({
  expanded,
  onToggle,
  moreLabel,
  lessLabel = 'Show less',
  context,
  nameSuffix,
  tone,
}: {
  expanded: boolean;
  onToggle: () => void;
  moreLabel: string;
  lessLabel?: string;
  /** Appended to the spoken label so several of the same button are told apart. */
  context?: string;
  /**
   * Finishes the visible words into the spoken label, "Collapse all" heard as "Collapse all
   * answers", so the name still starts with what a sighted reader sees.
   */
  nameSuffix?: string;
  tone: 'primary' | 'secondary';
}) {
  const visible = expanded ? lessLabel : moreLabel;

  const name = nameSuffix ? `${visible} ${nameSuffix}` : visible;

  const color = tone === 'primary' ? 'text-primary-700' : 'text-secondary-700';

  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      aria-expanded={expanded}
      accessibilityLabel={context ? `${name}, ${context}` : name}
      className={twJoin(TOUCH_TARGET_MIN_H, 'justify-center self-start web:cursor-pointer')}>
      <HStack className="items-center gap-1">
        <Text size="sm" className={twJoin('font-body font-semibold', color)}>
          {visible}
        </Text>

        <Icon as={expanded ? ChevronUp : ChevronDown} size="sm" className={color} />
      </HStack>
    </Pressable>
  );
}

/**
 * Names the speaker of a reply in words, so who is talking never rests on color or the avatar.
 */
const speakerName = (reply: LibraryPostReply, position: number) =>
  reply.role === 'submitter' ? 'Submitter' : specialistName(position);

/**
 * The day an answer was given, for its card header: the first reply's day when the specialist
 * opens the answer. Null when the submitter opens it, since that reply carries its own byline and
 * date, and when the day is unknown.
 */
const answerTime = (answer: LibraryPostAnswer) => {
  const [first] = answer.replies;

  return first?.role === 'specialist' ? replyDate(first.sentAt) : null;
};

/**
 * An answer card header's name with the time beside it. The time follows the name on the same
 * line and wraps directly under the name when the line is too narrow, never under the avatar.
 * Nothing at all when the time is unknown, which is common for legacy rows, so a missing `sentAt`
 * never shows as a blank or a guess. On the gray strip, `background-50`, small muted text needs
 * `typography-700` to clear 4.5:1 in light. Replies take the comment layout instead.
 */
function NameAndTime({ name, time }: { name: ReactNode; time: string | null }) {
  return (
    <HStack className="min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
      {name}

      {time ? (
        <Text size="xs" className="font-body text-typography-700">
          {time}
        </Text>
      ) : null}
    </HStack>
  );
}

/**
 * One reply in the comment layout, through the same `ConversationEntry` a comment draws with: the
 * avatar, the name as a heading with the exact time on the line under it, where a comment puts its
 * author's role, then the text under the name. The time sits below rather than beside the name so
 * "Primum Specialist 1" never wraps on a phone. Unlike a comment it has no vertical line, since
 * the answer card already groups the replies. No Like, no Reply and no "@name", since a reader
 * cannot act on a library answer. A follow-up shows the day it was sent, never "2 days ago", only
 * when that day differs from the answer's, since the order already tells the story within a day.
 *
 * The Submitter is the pale green avatar. A Primum Specialist is its number's brand color with no
 * badge, because the name beside it carries the number. Both are 32px, near a comment's 36px. The
 * name is h5, under the h4 of the answer card it sits in.
 */
function ReplyItem({
  reply,
  position,
  answerDay = null,
  hideReferences = false,
}: {
  reply: LibraryPostReply;
  position: number;
  /** The answer's own day. A reply sent that same day shows no date. */
  answerDay?: string | null;
  /** Leave out a reference list written into the text, as the answer lists its own. */
  hideReferences?: boolean;
}) {
  const day = replyDate(reply.sentAt);

  const time = day !== answerDay ? day : null;

  return (
    <ConversationEntry
      level={5}
      line={false}
      avatar={
        reply.role === 'submitter' ? (
          <SpecialistAvatar role="submitter" />
        ) : (
          <SpecialistAvatar role="specialist" position={position} badge={false} />
        )
      }
      name={speakerName(reply, position)}
      meta={time ? <Text className="text-xs text-typography-700">{time}</Text> : null}
      body={
        <AnswersMarkdown
          text={reply.body}
          className={CONVERSATION_BODY_TEXT_CLASS}
          hideReferences={hideReferences}
        />
      }
    />
  );
}

/**
 * The answer's first reply, in full: the answer itself. When the specialist opens, it has no
 * byline, because the card header already names the specialist and carries the time. When the
 * submitter opens, it takes the same byline as a follow-up, so the name and its time sit together
 * there too.
 */
function FirstReply({
  reply,
  position,
  hideReferences,
}: {
  reply: LibraryPostReply;
  position: number;
  hideReferences: boolean;
}) {
  if (reply.role === 'submitter') {
    return <ReplyItem reply={reply} position={position} hideReferences={hideReferences} />;
  }

  return <AnswersMarkdown text={reply.body} hideReferences={hideReferences} />;
}

/**
 * The back and forth after an answer: every reply, in order, continues inside the answer card
 * after the first reply, each in the comment layout without its line. One `outline-200` rule sets
 * the group off from the answer, with no count label. Inside it, space and each reply's byline
 * separate one reply from the next, so there are no lines between them. None is
 * folded and none is merged, so two replies in a row from the same role stay two.
 *
 * The group is still named for a screen reader, "2 follow-up replies to Primum Specialist 1", so
 * the way in says whose answer it follows.
 */
function Conversation({
  answer,
  replies,
  hideReferences,
}: {
  answer: LibraryPostAnswer;
  replies: LibraryPostReply[];
  hideReferences: boolean;
}) {
  return (
    <VStack
      role="group"
      aria-label={followUpGroupName(answer.position, replies.length)}
      className="mx-4 mb-4 border-t border-outline-200 pt-4">
      <VStack role="list" className="gap-5">
        {replies.map((reply) => (
          <VStack key={reply.id} role="listitem">
            <ReplyItem
              reply={reply}
              position={answer.position}
              answerDay={replyDate(answer.replies[0]?.sentAt)}
              hideReferences={hideReferences}
            />
          </VStack>
        ))}
      </VStack>
    </VStack>
  );
}

/**
 * The specialist's references, the last section of their card after every reply, under the same
 * `outline-200` rule the follow-ups use. Folded by default to one row: the book icon in the avatar
 * column, "References" and a count chip, so the answer reads first and the evidence is one tap
 * away. Opened, each reference lines up with the reply text and is spaced like a reply, with a
 * type badge (Article, Trial or Web page) over its AMA citation. The citation links through its
 * printed identifier: its DOI, else its PMID, its NCT number or its URL. There are no numbers,
 * since nothing in the answer points to one, and no cards, since replies have none.
 *
 * The list gathers what the specialist cited anywhere in the conversation, so it sits at the end
 * and reads as their sources, not as backing for the main answer alone. Its fold lives with the
 * post's folds, so Collapse all closes it, but it does not register as collapsible: Expand all
 * means every answer is open, and a folded reference list should not turn that control around.
 */
/**
 * A citation sits one step under reply text and one step darker, in the app's own face, so it reads
 * as a source rather than part of the answer: 14/22 in `typography-900` against replies at 16/24
 * (14/24 on native) in `typography-800`.
 */
const REFERENCE_CITATION_CLASS = 'text-left font-body text-sm leading-[22px] text-typography-900';

function AnswerReferences({ answer }: { answer: LibraryPostAnswer }) {
  const foldId = `references-${answerKey(answer.id)}`;

  const folds = useAnswersFolds();

  const postId = useAnswersPostId();

  const open = folds.isOpen(foldId);

  const references = answer.references ?? [];

  const section = useMemo(() => answerReferenceSection(references), [references]);

  const kinds = useMemo(
    () =>
      references
        .filter((reference) => reference.citation.trim() !== '')
        .map((reference) => referenceKind(reference)),
    [references]
  );

  if (!section) {
    return null;
  }

  const count = section.entries.length;

  const onLinkPress = (href: string) => {
    if (postId === null) {
      return;
    }

    trackInquiryLibraryReferenceClicked({
      postId,
      specialistPosition: answer.position,
      referencePosition: section.entries.findIndex((entry) => entry.href === href) + 1,
      linkType: referenceLinkType(href),
    });
  };

  const onToggle = () => {
    if (postId !== null) {
      trackInquiryLibraryReferencesExpanded({
        postId,
        specialistPosition: answer.position,
        referenceCount: count,
        expanded: !open,
      });
    }

    folds.toggle(foldId);
  };

  const sources = count === 1 ? '1 source' : `${count} sources`;

  return (
    <VStack
      role="group"
      aria-label="References"
      className="mx-4 mb-4 border-t border-outline-200 pt-1">
      <Pressable
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        aria-expanded={open}
        accessibilityLabel={`References, ${sources}, ${specialistName(answer.position)}`}
        className={twJoin(TOUCH_TARGET_MIN_H, 'justify-center web:cursor-pointer')}>
        <HStack className="items-center gap-3">
          <HStack className="min-w-0 flex-1 items-center gap-2">
            <Text size="sm" className="font-body font-semibold text-secondary-800">
              References
            </Text>

            <Text
              size="xs"
              className="min-w-[22px] rounded-md bg-background-100 px-1.5 py-0.5 text-center font-body font-semibold text-typography-800">
              {count}
            </Text>
          </HStack>

          <Icon as={open ? ChevronUp : ChevronDown} size="sm" className="text-secondary-800" />
        </HStack>
      </Pressable>

      {open ? (
        <VStack role="list" className={twJoin('gap-5 pb-1 pt-1', READING_MEASURE)}>
          <LinkPressContext.Provider value={onLinkPress}>
            <LinkClassContext.Provider value="text-secondary-700">
              {section.entries.map((entry, index) => {
                const kind = kinds[index] ?? 'article';

                return (
                  <VStack key={index} role="listitem" className="items-start gap-2">
                    <Text
                      size="xs"
                      className={twJoin(
                        'rounded px-2 py-0.5 font-body font-medium uppercase tracking-wide',
                        REFERENCE_KIND_BADGE[kind]
                      )}>
                      {REFERENCE_KIND_LABELS[kind]}
                    </Text>

                    <Line nodes={referenceItemNodes(entry)} className={REFERENCE_CITATION_CLASS} />
                  </VStack>
                );
              })}
            </LinkClassContext.Provider>
          </LinkPressContext.Provider>
        </VStack>
      ) : null}
    </VStack>
  );
}

/**
 * One answer's body: the first reply in full in its own padded section, then the follow-ups under
 * a line, then the specialist's references under the same line, last in the card, whether or not
 * there are follow-ups. When the API lists the answer's references, a reference list written into
 * a reply's text is left out, so no paper shows twice.
 */
export function AnswerBody({ answer }: { answer: LibraryPostAnswer }) {
  const [first, ...rest] = answer.replies;

  const hideReferences = (answer.references ?? []).length > 0;

  return (
    <VStack>
      <VStack className="p-4">
        <FirstReply reply={first} position={answer.position} hideReferences={hideReferences} />
      </VStack>

      {rest.length > 0 ? (
        <Conversation answer={answer} replies={rest} hideReferences={hideReferences} />
      ) : null}

      <AnswerReferences answer={answer} />
    </VStack>
  );
}

/** Opens every collapsed answer and closed fold inside one, or closes them again. Never the case. */
function ExpandAllButton() {
  const folds = useAnswersFolds();

  const postId = useAnswersPostId();

  if (!folds.hasCollapsible) {
    return null;
  }

  return (
    <FoldButton
      expanded={folds.allOpen}
      onToggle={() => {
        if (postId !== null) {
          trackInquiryLibraryAnswersToggled({ postId, expanded: !folds.allOpen });
        }

        if (folds.allOpen) {
          folds.closeAll();
        } else {
          folds.openAll();
        }
      }}
      moreLabel="Expand all"
      lessLabel="Collapse all"
      nameSuffix="answers"
      tone="primary"
    />
  );
}

/**
 * The answers heading row: the count on the left and Collapse all, or Expand all, on the right, in
 * one row that opens the answers section, below the question. The heading takes the room it needs
 * and wraps inside its own box before it pushes the button off the row.
 */
export function AnswersHeading({ count }: { count: number }) {
  return (
    <HStack className="items-center justify-between gap-4">
      <Heading size="h3-md" className="min-w-0 shrink font-body font-medium text-secondary-800">
        {count === 1 ? '1 Primum Specialist answer' : `${count} Primum Specialist answers`}
      </Heading>

      <ExpandAllButton />
    </HStack>
  );
}

/**
 * The header row of an answer card: the Primum Specialist's avatar, the no-photo placeholder in a
 * ring of its number's brand color with no number badge, then the name as a 16px semibold heading
 * with the answer's time beside it, wrapping under the name on a narrow screen. The avatar is decorative and hidden
 * from screen readers, so the name is the label. V1 has no profile line under the name.
 *
 * With two or more answers the whole row is a button that collapses the card to this header or
 * opens it again. It says which with `aria-expanded` and names the body it controls with
 * `aria-controls`. Its name starts with the heading's own words, then the time when there is one,
 * so the label a sighted reader sees is in it. A lone answer has nothing to collapse and keeps a
 * plain row.
 */
export function SpecialistHeader({ answer }: { answer: LibraryPostAnswer }) {
  const folds = useAnswersFolds();

  const postId = useAnswersPostId();

  const expanded = !folds.isAnswerCollapsed(answerKey(answer.id));

  const name = specialistName(answer.position);

  const time = answerTime(answer);

  // No number badge: the name beside it carries the number.
  const avatar = <SpecialistAvatar role="specialist" position={answer.position} badge={false} />;

  if (!folds.answersCollapse) {
    return (
      <HStack className={twJoin(TOUCH_TARGET_MIN_H, 'items-center gap-3')}>
        {avatar}

        <NameAndTime
          time={time}
          name={
            <Heading
              id={answerHeadingId(answer.position)}
              size="h4-md"
              className="min-w-0 font-body font-semibold text-secondary-800">
              {name}
            </Heading>
          }
        />
      </HStack>
    );
  }

  const row = (
    <Pressable
      onPress={() => {
        if (postId !== null) {
          trackInquiryLibraryAnswerCollapsed({
            postId,
            specialistPosition: answer.position,
            collapsed: expanded,
          });
        }

        folds.toggleAnswer(answerKey(answer.id));
      }}
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      aria-expanded={expanded}
      aria-controls={answerBodyId(answer.position)}
      accessibilityLabel={time ? `${name}, ${time}` : name}
      className={twJoin(TOUCH_TARGET_MIN_H, 'w-full justify-center web:cursor-pointer')}>
      <HStack className="items-center gap-3">
        {avatar}

        <NameAndTime
          time={time}
          name={
            <Text size="md" className="min-w-0 font-body font-semibold text-secondary-800">
              {name}
            </Text>
          }
        />

        <Icon as={expanded ? ChevronUp : ChevronDown} size="sm" className="text-primary-700" />
      </HStack>
    </Pressable>
  );

  /**
   * On a phone a Heading is a Text, and a Pressable nested inside a Text does not get its 48px
   * minimum height, so the strip came out about 37px tall with the avatar clipped at the top. A
   * View marked as a header holds the row there instead. On web the Heading stays an h4.
   */
  if (!isWeb) {
    return (
      <View accessibilityRole="header" nativeID={answerHeadingId(answer.position)}>
        {row}
      </View>
    );
  }

  return (
    <Heading id={answerHeadingId(answer.position)} size="h4-md" className="font-body">
      {row}
    </Heading>
  );
}

/**
 * The question card's header row: the submitter's avatar, the pale green inverse, unringed, with no
 * badge, and the label as a 16px semibold heading, the same size as an answer's header and the
 * same 48px row, so the two strips are the same height. Both are neutral, because secondary marks
 * Primum or AI content and the submitter is neither. The same avatar marks the submitter on every
 * reply. Role never rests on the avatar alone: the label beside it names it. The question carries
 * no time in the API, so none shows here.
 */
export function QuestionHeader() {
  return (
    <HStack className={twJoin(TOUCH_TARGET_MIN_H, 'items-center gap-3')}>
      <SpecialistAvatar role="submitter" />

      <Heading size="h3-md" className="min-w-0 flex-1 font-body font-semibold text-typography-900">
        Submitter's question
      </Heading>
    </HStack>
  );
}
